import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Star, ArrowRight, Shield, BarChart3, Bell, Loader2, Smartphone, Check, Phone, ChevronRight, LogIn, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const PLANS = [
  {
    name: 'Básico',
    price: '29,90',
    planId: 'basico',
    seal: 'Ideal para Pequenos Negócios',
    upgrade: 'Tudo do Free e mais...',
    features: ['Até 15 avaliações/mes', 'Dashboard Completo', 'Respostas via WhatsApp e E-mail', 'QR Code + Placas para Balcão', '1 WhatsApp para alertas'],
    googleFeature: 'Alavanque sua Nota no Google Maps',
  },
  {
    name: 'Pro',
    price: '39,90',
    planId: 'pro',
    seal: 'Sua Marca em Destaque',
    upgrade: 'Tudo do Básico e mais...',
    features: ['Até 30 avaliações/mes', 'Sua Logo Própria na Placa e no Sistema', 'PDF A4 para Impressão', 'Respostas via WhatsApp e E-mail', '2 WhatsApp para alertas'],
    googleFeature: 'Alavanque sua Nota no Google Maps',
  },
  {
    name: 'Empresarial',
    price: '59,90',
    planId: 'enterprise',
    best: true,
    seal: 'Mais Popular',
    upgrade: 'Tudo do Pro e mais...',
    features: ['Até 60 avaliações/mes', 'Relatórios em Excel para gestão profissional', 'Suporte Prioritário', 'Controle de Ocultar Avaliações', '3 WhatsApp para alertas'],
    googleFeature: 'Alavanque sua Nota no Google Maps',
  },
  {
    name: 'Elite',
    price: '99,90',
    planId: 'elite',
    seal: 'Controle Total',
    upgrade: 'Tudo do Empresarial e mais...',
    features: ['Até 100 avaliações/mes', 'Alerta de Crise Instantâneo', 'Notificação imediata de notas baixas', 'Controle de Ocultar Avaliações', '4 WhatsApp para alertas'],
    googleFeature: 'Alavanque sua Nota no Google Maps',
  },
  {
    name: '5 Estrelas',
    price: '149,90',
    planId: 'five_stars',
    complete: true,
    seal: 'Experiência Completa',
    upgrade: 'Tudo do Elite e mais...',
    features: ['Avaliações ILIMITADAS', 'Suporte VIP via WhatsApp', 'Controle de Ocultar Avaliações', 'WhatsApp ILIMITADOS'],
    googleFeature: 'Alavanque sua Nota no Google Maps',
  },
  {
    name: 'Free',
    price: '0',
    planId: 'free',
    isFree: true,
    seal: 'Gratuito por 60 dias',
    features: ['Até 10 avaliações/mes', 'Dashboard Completo', 'Respostas via E-mail', 'QR Code + Placas para Balcão', '1 WhatsApp para alertas'],
    googleFeature: 'Alavanque sua Nota no Google Maps',
    freeBadge: true,
  },
];

const PartnerLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const res = await fetch(`${API}/partners/${slug}/info`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setPartner(data);
        const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `ref_partner=${data.slug}; expires=${expires}; path=/`;
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPartner();
  }, [slug]);

  const scrollToPlans = () => {
    document.getElementById('partner-pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePartnerLogin = async () => {
    if (!loginEmail.trim()) { setLoginError('Digite seu email'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/partners/${slug}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      window.location.href = `/parceiro/painel/${data.panel_token}`;
    } catch (e) {
      setLoginError(e.message || 'Email não encontrado');
    } finally { setLoginLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-6">
        <div className="text-center">
          <img src={LOGO_URL} alt="FaloComODono" className="h-14 mx-auto mb-6 object-contain" />
          <p className="text-white/60 text-lg">Parceiro não encontrado.</p>
          <Button onClick={() => navigate('/')} className="mt-6 bg-[#C5A059] text-[#0A2647] font-bold">
            Ir para o Site
          </Button>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Star, title: 'Avaliação Inteligente', desc: 'Seus clientes avaliam em segundos pelo celular. QR Code exclusivo para cada estabelecimento.' },
    { icon: BarChart3, title: 'Dashboard em Tempo Real', desc: 'Acompanhe notas, tendencias e feedbacks detalhados num painel completo e fácil de usar.' },
    { icon: Bell, title: 'Alertas Instantâneos', desc: 'Receba notificacoes por WhatsApp e Email quando uma avaliação critica chegar.' },
    { icon: Shield, title: 'Gestão de Reputação', desc: 'Convide automaticamente clientes satisfeitos a avaliar no Google Maps.' },
  ];

  return (
    <div className="min-h-screen bg-[#0A2647] overflow-hidden" data-testid="partner-landing-page">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A2647] via-[#0e3260] to-[#0A2647]" />
        <div className="relative max-w-4xl mx-auto px-6 pt-10 pb-8 text-center">
          {/* Top bar with logo + partner access */}
          <div className="flex items-center justify-between mb-4">
            <div />
            <img src={LOGO_URL} alt="FaloComODono" className="h-16 object-contain" />
            <button onClick={() => setShowLogin(true)}
              className="flex items-center gap-2 text-sm font-medium text-[#C5A059] border border-[#C5A059]/30 hover:bg-[#C5A059]/10 rounded-full px-4 py-2 transition-all"
              data-testid="partner-access-btn">
              <LogIn className="w-4 h-4" />Area do Parceiro
            </button>
          </div>
          {partner?.name && (
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/25 rounded-full px-5 py-2 mb-8">
              <Smartphone className="w-4 h-4 text-[#C5A059]" />
              <span className="text-sm font-medium text-[#C5A059]">Indicação {partner.name}</span>
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-5">
            Saiba o que seus clientes{' '}
            <span className="text-[#C5A059]">realmente pensam</span>
          </h1>
          <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed">
            O FaloComODono coleta avaliações reais dos seus clientes, envia alertas em tempo real
            e te ajuda a melhorar a cada dia. Simples, rápido e poderoso.
          </p>
          <Button
            onClick={scrollToPlans}
            className="h-16 px-10 text-lg font-bold bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] rounded-2xl shadow-2xl shadow-[#C5A059]/20 transition-all hover:scale-105 active:scale-95"
            data-testid="partner-cta-btn"
          >
            Ver Planos e Preços
            <ArrowRight className="w-6 h-6 ml-3" />
          </Button>
          <button onClick={scrollToPlans} className="text-sm text-slate-400 hover:text-[#C5A059] transition-colors flex items-center gap-1 mt-4 mx-auto" data-testid="hero-plans-link">
            Comece gratuitamente com ate 10 avaliações por mes <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/[0.08] transition-all"
                data-testid={`partner-feature-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#C5A059]/15 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#C5A059]" />
                </div>
                <h3 className="text-white font-bold text-base mb-2">{f.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pricing Section - Full plan grid identical to LandingPage */}
      <section id="partner-pricing-section" className="py-20 lg:py-28 bg-[#0A2647] relative overflow-hidden" data-testid="partner-pricing-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C5A059]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#C5A059]/[0.03] rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 lg:mb-16">
            <p className="text-sm font-semibold text-[#C5A059] uppercase tracking-[0.2em] mb-4">Investimento</p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Planos e Preços
            </h2>
            <p className="text-base text-slate-400 max-w-lg mx-auto">
              Escolha o plano ideal e comece a transformar seu atendimento hoje.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4 max-w-7xl mx-auto">
            {PLANS.map((plan) => {
              const isHighlighted = plan.best || plan.complete;
              return (
                <div
                  key={plan.planId || plan.name}
                  className={`rounded-2xl p-6 transition-all duration-300 relative flex flex-col ${
                    isHighlighted
                      ? 'bg-white text-slate-900 shadow-[0_20px_60px_rgba(0,0,0,0.3)] ring-2 ring-[#C5A059]'
                      : plan.isFree
                      ? 'bg-white/[0.04] backdrop-blur-sm text-white border border-white/10 hover:border-[#C5A059]/30 hover:bg-white/[0.06]'
                      : 'bg-white/[0.08] backdrop-blur-sm text-white border border-white/10 hover:border-[#C5A059]/30 hover:bg-white/[0.1]'
                  }`}
                  data-testid={`plan-card-${plan.planId || plan.name}`}
                >
                  {plan.best && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#C5A059] text-[#0A2647] px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                        MAIS POPULAR
                      </span>
                    </div>
                  )}
                  {plan.complete && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#0A2647] text-[#C5A059] px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap border border-[#C5A059]/40">
                        MAIS COMPLETO
                      </span>
                    </div>
                  )}
                  <h3 className="text-base font-bold mb-1 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>{plan.name}</h3>
                  {plan.seal && (
                    <p className={`text-center text-[10px] font-semibold uppercase tracking-wider mb-2 ${isHighlighted ? 'text-[#C5A059]' : 'text-[#C5A059]/80'}`}>{plan.seal}</p>
                  )}
                  {!plan.isFree && (
                    <div className="text-center mb-2">
                      <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-500 text-white">Assine Agora</span>
                    </div>
                  )}
                  <div className="text-center mb-4 mt-1">
                    {plan.isFree ? (
                      <span className="text-3xl font-extrabold text-[#C5A059]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Grátis</span>
                    ) : (
                      <>
                        <span className="text-3xl font-extrabold" style={{ fontFamily: 'Montserrat, sans-serif' }}>R$ {plan.price}</span>
                        <span className={isHighlighted ? 'text-slate-500 text-xs' : 'text-slate-400 text-xs'}>/mes</span>
                      </>
                    )}
                  </div>
                  {plan.upgrade && (
                    <p className={`text-center text-[11px] font-medium mb-3 pb-3 border-b ${isHighlighted ? 'text-slate-500 border-slate-200' : 'text-slate-400 border-white/10'}`}>
                      {plan.upgrade}
                    </p>
                  )}
                  {plan.freeBadge && (
                    <div className="mb-3 py-2 px-3 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/30 text-center">
                      <p className="text-[10px] font-bold text-[#C5A059] uppercase tracking-wider">Inclui Automação de Reputação no Google</p>
                    </div>
                  )}
                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#C5A059]" />
                        <span className={isHighlighted ? 'text-slate-600' : 'text-slate-300'}>{f}</span>
                      </li>
                    ))}
                    {plan.googleFeature && (
                      <li className="flex items-start gap-2 text-xs">
                        <Star className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#C5A059] fill-[#C5A059]" />
                        <span className={`font-bold ${isHighlighted ? 'text-[#0A2647]' : 'text-[#C5A059]'}`}>{plan.googleFeature}</span>
                      </li>
                    )}
                    {['free', 'básico', 'pro', 'enterprise', 'elite', 'five_stars'].includes(plan.planId) && (
                      <li className="flex items-start gap-2 text-xs" data-testid={`wa-highlight-${plan.planId}`}>
                        <Phone className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-400" />
                        <span className={`font-bold ${isHighlighted ? 'text-emerald-700' : 'text-emerald-400'}`}>Alerta de Crise + Resumo Semanal via WhatsApp</span>
                      </li>
                    )}
                  </ul>
                  <Link to={plan.isFree ? '/register' : `/checkout/${plan.planId}`}>
                    <Button
                      className="w-full h-10 font-bold rounded-xl text-xs transition-all duration-300 bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] hover:shadow-[0_0_24px_rgba(197,160,89,0.3)]"
                      data-testid={`plan-cta-${plan.planId || plan.name}`}
                    >
                      {plan.isFree ? 'Ativar Agora' : 'Assinar Plano'}
                    </Button>
                  </Link>
                  {plan.isFree && (
                    <p className="text-[10px] text-center mt-2 text-slate-400 leading-snug" data-testid="free-microcopy">
                      Sem cartão de crédito. Comece a ouvir seus clientes em 2 minutos.
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10" data-testid="whatsapp-consultant">
            <a
              href="https://wa.me/5531991316297"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold px-8 py-4 rounded-2xl text-sm transition-all duration-300 hover:shadow-[0_8px_32px_rgba(37,211,102,0.35)] active:scale-[0.98]"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Dúvidas sobre o plano ideal? Fale com nosso Consultor
            </a>
          </div>
          <p className="text-center text-xs text-slate-500 mt-6">Plano Free gratuito com ate 10 avaliações/mes. Upgrade a qualquer momento. Cancele quando quiser.</p>
        </div>
      </section>

      {/* Footer */}
      <div className="text-center py-6 border-t border-white/5">
        <p className="text-xs text-white/20">
          FaloComODono - Plataforma de Avaliações para Empresas
        </p>
      </div>

      {/* Partner Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="partner-login-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowLogin(false)} />
          <div className="relative bg-[#0A2647] border border-[#C5A059]/30 rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <button onClick={() => setShowLogin(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#C5A059]/15 flex items-center justify-center mx-auto mb-3">
                <LogIn className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h3 className="text-white font-bold text-lg">Area do Parceiro</h3>
              <p className="text-slate-400 text-xs mt-1">Digite o email cadastrado como parceiro</p>
            </div>
            <div className="space-y-3">
              <Input
                type="email"
                value={loginEmail}
                onChange={e => { setLoginEmail(e.target.value); setLoginError(''); }}
                onKeyDown={e => e.key === 'Enter' && handlePartnerLogin()}
                placeholder="seu@email.com"
                className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                data-testid="partner-login-email"
              />
              {loginError && <p className="text-red-400 text-xs" data-testid="partner-login-error">{loginError}</p>}
              <Button onClick={handlePartnerLogin} disabled={loginLoading}
                className="w-full h-11 bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-bold text-sm"
                data-testid="partner-login-submit">
                {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Acessar Meu Painel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerLanding;
